// Notification preferences (06 §Notification preferences; SEARCH-NOTIF 2026-10-21, reworked by
// INBOX-PREFS, founder-approved 2026-10-29, ClickUp-style). The rules are pure: the `proj.inbox`
// consumer, the `inbox.digest` and `inbox.nudge` jobs, push, the API's routes and the web's
// in-memory twin all read them, so they agree.
//
// A person routes each notification TYPE (a category of inbox items) to destinations:
//
//   inbox     the item is projected at all (off: nowhere — no alert, no digest either)
//   alert     a browser / desktop notification (push) when it arrives
//   email     an email per item — no such channel exists yet (`EMAIL_AVAILABLE`): stored, never sent
//   digest    the item joins the digest email
//
// Resolution: the person's preset (null: the company's default preset, an admin's choice; none:
// `manager`) gives every row, then the person's own row edits win key by key. Alert, email and
// digest ride on the inbox item (inbox off: all off).
//
// The Waiting-on-you floor: a waiting item (an approval to decide, a question or reply a DE needs)
// always reaches the inbox and, with alerts off, the digest — so an agent never waits unseen.
//
// Quiet hours and the pause (1 hour, today, until Monday) hold alerts and the digest email; the
// inbox still fills. Per-item overrides (a task or a DE: all / mentions only / mute; channels use
// their own level, levels.ts) drop items before routing — never a waiting item.

export const NOTIFY_CATEGORIES = [
  "waiting",
  "mentions",
  "dms",
  "tasks",
  "de_activity",
  "kpis",
  "reports",
  "meetings",
  "followed",
] as const;
export type NotifyCategory = (typeof NOTIFY_CATEGORIES)[number];

export const NOTIFY_PRESETS = ["everything", "needs_me", "manager", "quiet"] as const;
export type NotifyPreset = (typeof NOTIFY_PRESETS)[number];

export const NOTIFY_DESTINATIONS = ["inbox", "alert", "email", "digest"] as const;
export type NotifyDestination = (typeof NOTIFY_DESTINATIONS)[number];
export type Route = Record<NotifyDestination, boolean>;
export type RoutePatch = Partial<Record<NotifyDestination, boolean | undefined>>;

/** No per-item email transport exists today: the column shows, disabled. */
export const EMAIL_AVAILABLE = false;

/** The platform's default preset (a company without its own). */
export const DEFAULT_PRESET: NotifyPreset = "manager";
/** Hours a waiting item waits before its one gentle reminder (company setting). */
export const DEFAULT_NUDGE_HOURS = 4;

export type OverrideLevel = "all" | "mentions" | "mute";
export type OverrideSubject = "task" | "agent";

export type DigestFrequency = "off" | "hourly" | "daily" | "weekly";
export const DEFAULT_DIGEST_FREQUENCY: DigestFrequency = "hourly";

/** Short labels for the settings grid (explanations sit behind (i) popovers). */
export const CATEGORY_LABELS: Record<NotifyCategory, string> = {
  waiting: "Waiting on you",
  mentions: "@mentions",
  dms: "Direct messages",
  tasks: "Tasks",
  de_activity: "DE activity",
  kpis: "KPIs",
  reports: "Reports",
  meetings: "Meetings",
  followed: "Followed activity",
};
export const CATEGORY_HINTS: Record<NotifyCategory, string> = {
  waiting: "Questions, approvals and replies a DE needs from you. Never fully off.",
  mentions: "Someone @mentions you, your seat or your team.",
  dms: "Messages in your direct and group DMs.",
  tasks: "Assigned to you, due soon, overdue, status changes and comments.",
  de_activity: "A DE finished, failed or started work.",
  kpis: "Numbers off track, check-ins and data entry due, budget alerts.",
  reports: "Reports to send or sign off.",
  meetings: "Meetings and calendar.",
  followed: "New messages in channels and topics you follow.",
};
export const PRESET_LABELS: Record<NotifyPreset, string> = {
  everything: "Everything",
  needs_me: "Only what needs me",
  manager: "Manager",
  quiet: "Quiet",
};
export const DESTINATION_LABELS: Record<NotifyDestination, string> = {
  inbox: "Inbox",
  alert: "Alert",
  email: "Email",
  digest: "Digest",
};

const R = (inbox: boolean, alert: boolean, digest: boolean): Route => ({
  inbox,
  alert,
  email: false,
  digest,
});
const ALL = R(true, true, true);
const NONE = R(false, false, false);

/** The presets, row by row. */
export const PRESETS: Record<NotifyPreset, Record<NotifyCategory, Route>> = {
  everything: Object.fromEntries(NOTIFY_CATEGORIES.map((c) => [c, ALL])) as Record<
    NotifyCategory,
    Route
  >,
  needs_me: {
    waiting: ALL,
    mentions: R(true, true, false),
    dms: R(true, true, false),
    tasks: R(true, false, true),
    de_activity: NONE,
    kpis: R(true, false, true),
    reports: ALL,
    meetings: R(true, true, false),
    followed: NONE,
  },
  manager: {
    waiting: ALL,
    mentions: ALL,
    dms: R(true, true, false),
    tasks: R(true, false, true),
    de_activity: R(true, false, true),
    kpis: R(true, false, true),
    reports: ALL,
    meetings: R(true, true, false),
    followed: R(true, false, false),
  },
  quiet: {
    waiting: R(true, false, true),
    mentions: R(true, false, true),
    dms: R(true, false, true),
    tasks: R(true, false, false),
    de_activity: NONE,
    kpis: R(true, false, false),
    reports: R(true, false, true),
    meetings: R(true, false, false),
    followed: NONE,
  },
};

/** What is stored per person (any key may be absent), plus the company's default preset. */
export type StoredPrefs = {
  /** Null: the company's default preset. */
  preset: NotifyPreset | null;
  /** The person's own edits over the preset, per row. */
  routes: Partial<Record<string, RoutePatch>>;
  quietStart: number | null;
  quietEnd: number | null;
  timeZone: string | null;
  digestFrequency: DigestFrequency;
  /** Alerts and the digest hold until then. */
  pausedUntil: Date | null;
  /** `task:<id>` / `agent:<membership id>` → level. */
  overrides: Partial<Record<string, OverrideLevel>>;
  /** The company's default preset (resolved by the reader). */
  companyPreset: NotifyPreset;
};

export const DEFAULT_PREFS: StoredPrefs = {
  preset: null,
  routes: {},
  quietStart: null,
  quietEnd: null,
  timeZone: null,
  digestFrequency: DEFAULT_DIGEST_FREQUENCY,
  pausedUntil: null,
  overrides: {},
  companyPreset: DEFAULT_PRESET,
};

export const isPreset = (v: unknown): v is NotifyPreset =>
  typeof v === "string" && (NOTIFY_PRESETS as readonly string[]).includes(v);
export const isCategory = (v: unknown): v is NotifyCategory =>
  typeof v === "string" && (NOTIFY_CATEGORIES as readonly string[]).includes(v);

/** The preset in force for a person. */
export const effectivePreset = (p: StoredPrefs | null): NotifyPreset =>
  p?.preset ?? p?.companyPreset ?? DEFAULT_PRESET;

/** The Waiting-on-you floor: always in the inbox, and in the digest when it does not alert. */
function floor(c: NotifyCategory, r: Route): Route {
  if (c !== "waiting") return r;
  return { ...r, inbox: true, digest: r.digest || !r.alert };
}

/** One row, resolved: preset, then the person's edits, then the floor; the rest ride on inbox. */
export function routeFor(p: StoredPrefs | null, c: NotifyCategory): Route {
  const base = PRESETS[effectivePreset(p)][c];
  const own = p?.routes[c] ?? {};
  const merged: Route = {
    inbox: own.inbox ?? base.inbox,
    alert: own.alert ?? base.alert,
    email: own.email ?? base.email,
    digest: own.digest ?? base.digest,
  };
  const f = floor(c, merged);
  return {
    inbox: f.inbox,
    alert: f.inbox && f.alert,
    email: f.inbox && f.email && EMAIL_AVAILABLE,
    digest: f.inbox && f.digest,
  };
}

/** Every row, resolved. */
export function resolvedRoutes(p: StoredPrefs | null): Record<NotifyCategory, Route> {
  return Object.fromEntries(NOTIFY_CATEGORIES.map((c) => [c, routeFor(p, c)])) as Record<
    NotifyCategory,
    Route
  >;
}

/** A preset's rows exactly (the floor applied), to tell whether the person edited any row. */
export const presetRoutes = (preset: NotifyPreset): Record<NotifyCategory, Route> =>
  resolvedRoutes({ ...DEFAULT_PREFS, preset });

// Kinds → categories ---------------------------------------------------------------------------

/** Kinds that are waiting on the person (the floor; the "Waiting on me" tab). */
export const WAITING_KINDS = ["approval", "agent_escalation"] as const;

/**
 * The category of an inbox item. `dm`: it came from a direct or group DM. Null: a kind the person
 * cannot route (their own reminders, memory reviews, system notices) — inbox only.
 */
export function categoryOf(
  kind: string,
  subjectKind: string | null = null,
  dm = false,
): NotifyCategory | null {
  switch (kind) {
    case "approval":
    case "agent_escalation":
      return "waiting";
    case "mention":
      return dm ? "dms" : "mentions";
    case "reply":
      return dm ? "dms" : subjectKind === "task" ? "tasks" : "followed";
    case "assignment":
    case "task_change":
      return "tasks";
    case "agent_result":
      return "de_activity";
    case "kpi_due":
    case "kpi_correction":
    case "budget_alert":
      return "kpis";
    case "reminder":
      // A reporting duty's reminder, a task due soon or overdue; a reminder the person set: fixed.
      return subjectKind === "reporting_duty" ? "reports" : subjectKind === "task" ? "tasks" : null;
    default:
      return null;
  }
}

/** Fixed kinds: inbox only, no alert, no digest. */
const FIXED: Route = R(true, false, false);

/** An item's route (fixed kinds: inbox only). */
export function itemRoute(p: StoredPrefs | null, category: NotifyCategory | null): Route {
  return category ? routeFor(p, category) : FIXED;
}

// Compatibility: the SEARCH-NOTIF per-kind view ----------------------------------------------------

export const CONFIGURABLE_KINDS = [
  "mention",
  "reply",
  "assignment",
  "task_change",
  "approval",
  "agent_result",
  "kpi_due",
  "budget_alert",
] as const;
export type ConfigurableKind = (typeof CONFIGURABLE_KINDS)[number];
export type Channels = { inbox: boolean; email: boolean; desktop: boolean };
export type ChannelsPatch = {
  inbox?: boolean | undefined;
  email?: boolean | undefined;
  desktop?: boolean | undefined;
};

/** The row a SEARCH-NOTIF kind maps to (its non-DM, non-task category). */
export const KIND_CATEGORY: Record<ConfigurableKind, NotifyCategory> = {
  mention: "mentions",
  reply: "followed",
  assignment: "tasks",
  task_change: "tasks",
  approval: "waiting",
  agent_result: "de_activity",
  kpi_due: "kpis",
  budget_alert: "kpis",
};

/** A kind's channels as SEARCH-NOTIF showed them: email = the digest, desktop = the alert. */
export function channelsFor(p: StoredPrefs | null, kind: string, subjectKind?: string): Channels {
  const r = itemRoute(p, categoryOf(kind, subjectKind ?? null));
  return { inbox: r.inbox, email: r.digest, desktop: r.alert };
}

export function resolvedTypes(p: StoredPrefs | null): Record<ConfigurableKind, Channels> {
  return Object.fromEntries(CONFIGURABLE_KINDS.map((k) => [k, channelsFor(p, k)])) as Record<
    ConfigurableKind,
    Channels
  >;
}

/** A per-kind patch as row edits (email → digest, desktop → alert). */
export function routesFromKinds(
  types: Partial<Record<string, ChannelsPatch>>,
): Partial<Record<NotifyCategory, RoutePatch>> {
  const out: Partial<Record<NotifyCategory, RoutePatch>> = {};
  for (const k of CONFIGURABLE_KINDS) {
    const t = types[k];
    if (!t) continue;
    const c = KIND_CATEGORY[k];
    out[c] = {
      ...out[c],
      ...(t.inbox !== undefined ? { inbox: t.inbox } : {}),
      ...(t.email !== undefined ? { digest: t.email } : {}),
      ...(t.desktop !== undefined ? { alert: t.desktop } : {}),
    };
  }
  return out;
}

// Overrides ------------------------------------------------------------------------------------

export const overrideKey = (s: OverrideSubject, id: string) => `${s}:${id}`;

/**
 * Whether a per-item override lets an item through. Mute: nothing but waiting items. Mentions
 * only: mentions, assignments and waiting items.
 */
export function overrideAllows(
  level: OverrideLevel | undefined,
  kind: string,
  category: NotifyCategory | null,
): boolean {
  if (!level || level === "all" || category === "waiting") return true;
  if (level === "mute") return false;
  return kind === "mention" || kind === "assignment";
}

// Time ----------------------------------------------------------------------------------------

/** "22:00" ↔ 1320 (minutes after local midnight). */
export function minutesOf(hhmm: string): number {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(hhmm);
  if (!m) throw new RangeError(`not a time of day: ${hhmm}`);
  return Number(m[1]) * 60 + Number(m[2]);
}

export function hhmmOf(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Whether `tz` is an IANA time zone this runtime knows. */
export function isTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

const zoneOf = (tz: string | null) => (tz && isTimeZone(tz) ? tz : "UTC");

/** The local calendar fields of `at` in `tz`. */
function localParts(at: Date, tz: string | null) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: zoneOf(tz),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    weekday: "short",
    hourCycle: "h23",
  }).formatToParts(at);
  const v = (t: string) => parts.find((p) => p.type === t)?.value ?? "0";
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return {
    y: Number(v("year")),
    mo: Number(v("month")),
    d: Number(v("day")),
    h: Number(v("hour")),
    mi: Number(v("minute")),
    s: Number(v("second")),
    /** Monday = 0. */
    dow: Math.max(0, days.indexOf(v("weekday"))),
  };
}

/** Minutes after local midnight of `at` in `tz` (UTC when the zone is unknown). */
export function localMinutes(at: Date, tz: string | null): number {
  const p = localParts(at, tz);
  return p.h * 60 + p.mi;
}

/** The instant of local midnight `days` days after `at`'s local date, in `tz`. */
function localMidnight(at: Date, tz: string | null, days: number): Date {
  const p = localParts(at, tz);
  const wall = Date.UTC(p.y, p.mo - 1, p.d + days);
  // The zone's offset at that wall time (twice: a DST change between `at` and then).
  let guess = wall;
  for (let i = 0; i < 2; i++) {
    const q = localParts(new Date(guess), tz);
    const shown = Date.UTC(q.y, q.mo - 1, q.d, q.h, q.mi, q.s);
    guess += wall - shown;
  }
  return new Date(guess);
}

export const PAUSE_CHOICES = ["1h", "today", "monday"] as const;
export type PauseChoice = (typeof PAUSE_CHOICES)[number];

/** When a pause chosen at `now` ends: an hour, the end of today, or the start of next Monday. */
export function pauseUntil(choice: PauseChoice, now: Date, tz: string | null): Date {
  if (choice === "1h") return new Date(now.getTime() + 3_600_000);
  if (choice === "today") return localMidnight(now, tz, 1);
  const dow = localParts(now, tz).dow;
  return localMidnight(now, tz, 7 - dow);
}

export const isPaused = (p: StoredPrefs | null, at: Date): boolean =>
  !!p?.pausedUntil && p.pausedUntil.getTime() > at.getTime();

/**
 * Whether `at` falls in the person's quiet hours: [start, end) in their local time, wrapping
 * past midnight when start > end (22:00–08:00). Equal ends, or none set: never quiet.
 */
export function inQuietHours(
  prefs: Pick<StoredPrefs, "quietStart" | "quietEnd" | "timeZone"> | null,
  at: Date,
): boolean {
  if (!prefs || prefs.quietStart === null || prefs.quietEnd === null) return false;
  const { quietStart: s, quietEnd: e } = prefs;
  if (s === e) return false;
  const m = localMinutes(at, prefs.timeZone);
  return s < e ? m >= s && m < e : m >= s || m < e;
}

/** Whether an item of `category` arriving at `at` alerts (its row, quiet hours, the pause). */
export function wantsAlert(p: StoredPrefs | null, category: NotifyCategory | null, at: Date) {
  return itemRoute(p, category).alert && !inQuietHours(p, at) && !isPaused(p, at);
}

/** SEARCH-NOTIF's name: a new item of `kind` asks for a desktop notification. */
export function wantsDesktop(
  p: StoredPrefs | null,
  kind: string,
  at: Date,
  subjectKind?: string,
): boolean {
  return wantsAlert(p, categoryOf(kind, subjectKind ?? null), at);
}

const MINUTE = 60_000;
const PERIOD: Record<Exclude<DigestFrequency, "off">, number> = {
  hourly: 60 * MINUTE,
  daily: 24 * 60 * MINUTE,
  weekly: 7 * 24 * 60 * MINUTE,
};
/** The job ticks every quarter hour; a tick a little early still counts as on time. */
const SLACK = 2 * MINUTE;

/** Whether the person's digest may be sent at `now` (frequency, last send, quiet hours, pause). */
export function digestDue(
  prefs: StoredPrefs | null,
  lastSent: Date | null,
  now: Date,
  /** The plan's frequency (`digestPlan`), when it differs from the stored one. */
  frequency?: DigestFrequency,
): boolean {
  const freq = frequency ?? prefs?.digestFrequency ?? DEFAULT_DIGEST_FREQUENCY;
  if (freq === "off") return false;
  if (inQuietHours(prefs, now) || isPaused(prefs, now)) return false;
  if (!lastSent) return true;
  return now.getTime() - lastSent.getTime() >= PERIOD[freq] - SLACK;
}

/** The categories a person's digest may carry. */
export function digestCategories(p: StoredPrefs | null): NotifyCategory[] {
  return NOTIFY_CATEGORIES.filter((c) => routeFor(p, c).digest);
}

/**
 * The digest a person gets: their frequency and rows. With the digest off, a Waiting-on-you row
 * that does not alert still goes out daily (the floor: an agent never waits unseen).
 */
export function digestPlan(p: StoredPrefs | null): {
  frequency: DigestFrequency;
  categories: NotifyCategory[];
} {
  const frequency = p?.digestFrequency ?? DEFAULT_DIGEST_FREQUENCY;
  if (frequency !== "off") return { frequency, categories: digestCategories(p) };
  return routeFor(p, "waiting").alert
    ? { frequency: "off", categories: [] }
    : { frequency: "daily", categories: ["waiting"] };
}

/** "3 things are waiting on you, oldest 2 days" (null: nothing waits). */
export function waitingLine(count: number, oldest: Date | null, now: Date): string | null {
  if (count < 1 || !oldest) return null;
  const hours = Math.max(0, Math.floor((now.getTime() - oldest.getTime()) / 3_600_000));
  const age =
    hours >= 48
      ? `${Math.floor(hours / 24)} days`
      : hours >= 24
        ? "1 day"
        : hours >= 1
          ? `${hours} hour${hours === 1 ? "" : "s"}`
          : "under an hour";
  return count === 1
    ? `1 thing is waiting on you, for ${age}`
    : `${count} things are waiting on you, oldest ${age}`;
}
